import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from "mobx";
import type moment from "moment";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import { BaseStore } from "stores/BaseStore";
import { UsageValueService } from "services/UsageValueService";
import type { UsageValueReport } from "services/UsageValueService";
import { rangeForPreset } from "./usageValueFormat";
import type { UsageValuePresetId, UsageValueRange } from "./usageValueFormat";

export type UsageValueTab = "chat" | "search" | "flow";

// One instance per organization page; stale responses cannot populate another range or org.
export default class OrganizationUsageValueStore extends BaseStore {
    readonly grid = new AcxDataGridStore();
    private readonly service = new UsageValueService();
    private abortController?: AbortController;

    @observable preset: UsageValuePresetId = "d90";
    @observable.ref range: UsageValueRange = rangeForPreset("d90");
    @observable tab: UsageValueTab = "chat";
    @observable query = "";
    @observable showInactive = false;
    @observable includeAuthentizens = false;
    @observable.ref report?: UsageValueReport;
    @observable isLoading = false;
    @observable error = "";

    constructor(readonly organizationId: string) {
        super("OrganizationUsageValueStore");
        makeObservable(this);
        this.grid.disableLocalConfig = true;
        this.grid.hideHeader = true;
        this.grid.checkboxSelection = false;
        this.grid.disableRowSelectionOnClick = true;
        this.grid.heightOverride = "560px";
        this.grid.rowHeightOverride = true;
    }

    @computed get filteredChatUsers() {
        const query = this.query.trim().toLowerCase();
        const rows = this.report?.chatUsers ?? [];
        if (!query) return rows;
        return rows.filter((row) =>
            [row.name, row.email, row.role].some((value) =>
                value?.toLowerCase().includes(query),
            ),
        );
    }

    @computed get visibleSearchSubscriptions() {
        const rows = this.report?.searchSubscriptions ?? [];
        return this.showInactive ? rows : rows.filter((row) => row.isActive);
    }

    @computed get visibleFlowSubscriptions() {
        const rows = this.report?.flowSubscriptions ?? [];
        return this.showInactive ? rows : rows.filter((row) => row.isActive);
    }

    @action selectPreset(preset: Exclude<UsageValuePresetId, "custom">) {
        this.preset = preset;
        this.range = rangeForPreset(preset);
        void this.load();
    }

    /** Shows the date picker; the current range stays until a custom one is picked. */
    @action chooseCustom() {
        this.preset = "custom";
    }

    /** Custom ranges arrive as inclusive days from the date picker. */
    @action selectCustomRange(start: moment.Moment, end: moment.Moment) {
        this.preset = "custom";
        this.range = {
            start: start.clone().startOf("day"),
            end: end.clone().startOf("day").add(1, "day"),
        };
        void this.load();
    }

    @action selectTab(tab: UsageValueTab) {
        this.tab = tab;
    }

    @action setQuery(query: string) {
        this.query = query;
    }

    @action toggleInactive() {
        this.showInactive = !this.showInactive;
    }

    @action toggleAuthentizens() {
        this.includeAuthentizens = !this.includeAuthentizens;
        void this.load();
    }

    @action dispose() {
        this.abortController?.abort();
    }

    @action async load() {
        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;
        this.isLoading = true;
        this.grid.isLoading = true;
        this.error = "";
        try {
            const report = await this.service.getReport(
                this.range.start.toDate(),
                this.range.end.toDate(),
                this.includeAuthentizens,
                controller.signal,
            );
            if (controller.signal.aborted) return;
            runInAction(() => {
                this.report = report;
            });
        } catch {
            if (controller.signal.aborted) return;
            runInAction(() => {
                this.report = undefined;
                this.error = "Unable to load usage data. Please try again.";
            });
        } finally {
            if (!controller.signal.aborted)
                runInAction(() => {
                    this.isLoading = false;
                    this.grid.isLoading = false;
                });
        }
    }
}
